import { create } from 'zustand'
import { storage } from '@/lib/storage'

export type ThemePreference = 'light' | 'dark' | 'system'

const THEME_KEY = 'oka.theme'
const SIDEBAR_KEY = 'oka.sidebarCollapsed'

function readTheme(): ThemePreference {
  const stored = storage.get(THEME_KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
}

export type SourcesPanelState = {
  open: boolean
  /** Stable key of the assistant message (client id while streaming, else message id). */
  messageKey: string | null
  /** Source to scroll to and highlight. */
  sourceId: string | null
  /** Bumped on every request so re-clicking the same chip re-highlights. */
  nonce: number
}

export type ViewerState = {
  /** Document open in the in-app viewer, or null. */
  docId: number | null
  /** Heading path to scroll to in the text view (from a cited source). */
  section: string | null
  nonce: number
}

type UiState = {
  theme: ThemePreference
  sidebarCollapsed: boolean
  mobileSidebarOpen: boolean
  sourcesPanel: SourcesPanelState
  viewer: ViewerState
  /** Department scope for new questions; null = all departments. */
  department: string | null
  composerFocusNonce: number
  searchFocusNonce: number
  announcement: { text: string; nonce: number }
  /** The main content is scrolled away from the top (the header shows its hairline). */
  contentScrolled: boolean

  setTheme: (theme: ThemePreference) => void
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setMobileSidebarOpen: (open: boolean) => void
  openSources: (messageKey: string, sourceId?: string | null) => void
  closeSources: () => void
  openViewer: (docId: number, section?: string | null) => void
  closeViewer: () => void
  setDepartment: (department: string | null) => void
  focusComposer: () => void
  focusSearch: () => void
  announce: (text: string) => void
  setContentScrolled: (scrolled: boolean) => void
}

export const useUiStore = create<UiState>()((set) => ({
  theme: readTheme(),
  sidebarCollapsed: storage.get(SIDEBAR_KEY) === '1',
  mobileSidebarOpen: false,
  sourcesPanel: { open: false, messageKey: null, sourceId: null, nonce: 0 },
  viewer: { docId: null, section: null, nonce: 0 },
  department: null,
  composerFocusNonce: 0,
  searchFocusNonce: 0,
  announcement: { text: '', nonce: 0 },
  contentScrolled: false,

  setTheme: (theme) => {
    storage.set(THEME_KEY, theme)
    set({ theme })
  },
  toggleSidebar: () =>
    set((state) => {
      const sidebarCollapsed = !state.sidebarCollapsed
      storage.set(SIDEBAR_KEY, sidebarCollapsed ? '1' : '0')
      return { sidebarCollapsed }
    }),
  setSidebarCollapsed: (sidebarCollapsed) => {
    storage.set(SIDEBAR_KEY, sidebarCollapsed ? '1' : '0')
    set({ sidebarCollapsed })
  },
  setMobileSidebarOpen: (mobileSidebarOpen) => set({ mobileSidebarOpen }),
  openSources: (messageKey, sourceId = null) =>
    set((state) => ({
      sourcesPanel: {
        open: true,
        messageKey,
        sourceId,
        nonce: state.sourcesPanel.nonce + 1,
      },
    })),
  closeSources: () => set((state) => ({ sourcesPanel: { ...state.sourcesPanel, open: false } })),
  openViewer: (docId, section = null) =>
    set((state) => ({ viewer: { docId, section, nonce: state.viewer.nonce + 1 } })),
  closeViewer: () => set((state) => ({ viewer: { ...state.viewer, docId: null } })),
  setDepartment: (department) => set({ department }),
  focusComposer: () => set((state) => ({ composerFocusNonce: state.composerFocusNonce + 1 })),
  focusSearch: () => set((state) => ({ searchFocusNonce: state.searchFocusNonce + 1 })),
  announce: (text) => set((state) => ({ announcement: { text, nonce: state.announcement.nonce + 1 } })),
  setContentScrolled: (contentScrolled) =>
    set((state) => (state.contentScrolled === contentScrolled ? state : { contentScrolled })),
}))
